<template>
  <transition name="fade">
    <div v-if="isSosAlertActive" class="sos-alert-container">
      <!-- Overlay con efecto intermitente -->
      <div class="sos-overlay"></div>

      <!-- Panel de alerta en la parte superior -->
      <div class="sos-alert-panel">
        <div class="sos-content">
          <div class="sos-icon-container">
            <i class="material-icons sos-icon">warning</i>
          </div>

          <div class="sos-message">
            <div class="sos-header">
              <h2 class="sos-title">🚨 ALERTA SOS ACTIVA 🚨</h2>
              <!-- Contador y navegación -->
              <div v-if="hasMultipleAlerts" class="sos-navigation">
                <div class="sos-counter">
                  {{ currentAlertNumber }} de {{ totalAlertsCount }}
                </div>
                <div class="sos-nav-buttons">
                  <button
                    @click="navigateToPrevious"
                    :disabled="!hasPreviousAlert"
                    class="sos-nav-button"
                    title="Alerta anterior"
                  >
                    <i class="material-icons">chevron_left</i>
                  </button>
                  <button
                    @click="navigateToNext"
                    :disabled="!hasNextAlert"
                    class="sos-nav-button"
                    title="Siguiente alerta"
                  >
                    <i class="material-icons">chevron_right</i>
                  </button>
                </div>
              </div>
            </div>
            <p class="sos-subtitle">Se ha recibido una señal de emergencia</p>

            <div v-if="sosAlertData" class="sos-details">
              <p class="sos-timestamp">
                <strong>Hora:</strong> {{ formatTimestamp }}
              </p>
              <div v-if="sosAlertData.licensePlate || sosAlertData.vehicle || vehicleCodeFromDevice" class="sos-vehicle-info">
                <p v-if="vehicleCodeFromDevice">
                  <strong>Código/Sigla:</strong> {{ vehicleCodeFromDevice }}
                </p>
                <p v-if="sosAlertData.licensePlate || sosAlertData.vehicle">
                  <strong>Placa:</strong> {{ sosAlertData.licensePlate || sosAlertData.vehicle }}
                </p>
              </div>
              <div v-if="sosAlertData.driver" class="sos-driver-info">
                <p><strong>Conductor:</strong> {{ sosAlertData.driver }}</p>
              </div>
            </div>
          </div>

          <div class="sos-actions">
            <!-- Primera fila: Botón principal de ubicar -->
            <div v-if="vehicleIdFromDevice" class="sos-actions-primary">
              <button
                @click="locateVehicle"
                class="sos-locate-button"
                title="Ubicar vehículo"
              >
                <i class="material-icons">my_location</i>
                <span>UBICAR VEHÍCULO</span>
              </button>
            </div>

            <!-- Segunda fila: Botones secundarios -->
            <div class="sos-actions-secondary">
              <button
                @click="showManageForm = !showManageForm"
                class="sos-manage-button"
                title="Gestionar alerta"
              >
                <i class="material-icons">assignment</i>
                <span>GESTIONAR</span>
              </button>

              <button
                @click="dismissAlert"
                class="sos-dismiss-button"
                title="Desactivar alerta"
              >
                <i class="material-icons">close</i>
                <span>DESACTIVAR</span>
              </button>
            </div>
          </div>
        </div>

        <!-- Formulario de gestión de alerta -->
        <transition name="slide-down">
          <div v-if="showManageForm" class="sos-manage-form">
            <div class="sos-form-divider"></div>
            <h3 class="sos-manage-title">Gestionar Alerta SOS</h3>

            <div class="sos-form-group">
              <label for="alert-type" class="sos-form-label">Clasificación de la alerta:</label>
              <select
                id="alert-type"
                v-model="manageFormData.alert_type"
                class="sos-form-select"
              >
                <option value="">Seleccione una opción</option>
                <option value="real_emergency">Emergencia real</option>
                <option value="accidental_activation">Activación accidental</option>
                <option value="test_maintenance">Prueba / Mantenimiento</option>
                <option value="could_not_confirm">No se pudo confirmar</option>
              </select>
            </div>

            <div class="sos-form-group">
              <label for="actions-taken" class="sos-form-label">
                Acciones tomadas y otras observaciones: <span class="required-field">*</span>
              </label>
              <textarea
                id="actions-taken"
                v-model="manageFormData.actions_taken"
                class="sos-form-textarea"
                rows="4"
                placeholder="Describa las acciones realizadas... (Campo obligatorio)"
                required
              ></textarea>
            </div>

            <div class="sos-form-actions">
              <button
                @click="saveManageForm"
                class="sos-save-button"
                :disabled="!manageFormData.alert_type || !manageFormData.actions_taken || isSavingManagement"
              >
                <i v-if="!isSavingManagement" class="material-icons">save</i>
                <i v-else class="material-icons rotating">sync</i>
                <span>{{ isSavingManagement ? 'GUARDANDO...' : 'GUARDAR' }}</span>
              </button>

              <button
                @click="cancelManageForm"
                class="sos-cancel-button"
              >
                <i class="material-icons">cancel</i>
                <span>CANCELAR</span>
              </button>
            </div>
          </div>
        </transition>
      </div>

      <!-- Bordes intermitentes -->
      <div class="sos-border sos-border-top"></div>
      <div class="sos-border sos-border-bottom"></div>
      <div class="sos-border sos-border-left"></div>
      <div class="sos-border sos-border-right"></div>
    </div>
  </transition>
</template>

<script>
import { mapGetters, mapActions } from 'vuex'

export default {
  name: 'SOSAlert',

  data() {
    return {
      showManageForm: false,
      manageFormData: {
        alert_type: '',
        actions_taken: ''
      },
      isSavingManagement: false
    }
  },

  computed: {
    ...mapGetters('sosAlerts', [
      'isSosAlertActive',
      'sosAlertData',
      'sosAlertTimestamp',
      'totalAlertsCount',
      'currentAlertNumber',
      'hasMultipleAlerts',
      'hasPreviousAlert',
      'hasNextAlert',
      'newAlertTrigger'
    ]),
    ...mapGetters(['vehicleCode', 'vehicleId']),

    formatTimestamp() {
      if (!this.sosAlertTimestamp) return ''
      const date = new Date(this.sosAlertTimestamp)
      return date.toLocaleString('es-ES', {
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        day: '2-digit',
        month: '2-digit',
        year: 'numeric'
      })
    },

    vehicleCodeFromDevice() {
      if (!this.sosAlertData) {
        console.warn('sosAlertData is null or undefined')
        return null
      }

      const deviceId = this.sosAlertData.deviceId
      if (!deviceId) {
        console.warn('deviceId is null or undefined in sosAlertData:', this.sosAlertData)
        return null
      }

      try {
        const code = this.vehicleCode(deviceId)
        return code || null
      } catch (error) {
        console.warn('Error obteniendo código del vehículo:', error)
        return null
      }
    },

    vehicleIdFromDevice() {
      if (!this.sosAlertData) {
        console.warn('sosAlertData is null or undefined')
        return null
      }

      const deviceId = this.sosAlertData.deviceId
      if (!deviceId) {
        console.warn('deviceId is null or undefined in sosAlertData:', this.sosAlertData)
        return null
      }

      try {
        const id = this.vehicleId(deviceId)
        return id || null
      } catch (error) {
        console.warn('Error obteniendo ID del vehículo:', error)
        return null
      }
    }
  },

    watch: {
    isSosAlertActive(newVal) {
      if (newVal) {
        // Agregar clase al body cuando la alerta está activa
        document.body.classList.add('sos-alert-active')
      } else {
        // Remover clase del body cuando se desactiva
        document.body.classList.remove('sos-alert-active')
      }
    },

    // Reproducir sonido cada vez que llega una nueva alerta
    newAlertTrigger(newVal, oldVal) {
      if (newVal > oldVal) {
        console.log('🔊 Nueva alerta SOS detectada, reproduciendo sonido...')
        this.playAlertSound()
      }
    }
  },

    methods: {
    ...mapActions('sosAlerts', [
      'deactivateSosAlert',
      'navigateToPreviousAlert',
      'navigateToNextAlert'
    ]),

    /**
     * Navega a la vista del vehículo sin cerrar la alerta SOS
     * La vista del vehículo se encarga de cargar sus propios datos
     */
    locateVehicle() {
      console.log('🚗 === UBICAR VEHÍCULO DEBUG ===')
      console.log('currentAlertIndex:', this.currentAlertIndex)
      console.log('currentAlertNumber:', this.currentAlertNumber)
      console.log('totalAlertsCount:', this.totalAlertsCount)
      console.log('sosAlertData completo:', JSON.stringify(this.sosAlertData, null, 2))

      if (!this.sosAlertData) {
        console.error('❌ sosAlertData es null o undefined')
        return
      }

      // Intentar obtener el vehicleId de diferentes maneras
      let vehicleId = null

      // Método 1: Usar la propiedad computada
      if (this.vehicleIdFromDevice) {
        vehicleId = this.vehicleIdFromDevice
        console.log('✅ VehicleId obtenido via propiedad computada:', vehicleId)
      }

      // Método 2: Intentar obtenerlo directamente del deviceId
      if (!vehicleId && this.sosAlertData.deviceId) {
        try {
          vehicleId = this.vehicleId(this.sosAlertData.deviceId)
          console.log('✅ VehicleId obtenido directamente del deviceId:', vehicleId)
        } catch (error) {
          console.warn('⚠️ Error obteniendo vehicleId del deviceId:', error)
        }
      }

      // Método 3: Buscar en otros campos de la alerta
      if (!vehicleId) {
        console.log('🔍 Buscando vehicleId en otros campos de sosAlertData...')
        if (this.sosAlertData.vehicle_id) {
          vehicleId = this.sosAlertData.vehicle_id
          console.log('✅ VehicleId encontrado en vehicle_id:', vehicleId)
        } else if (this.sosAlertData.vehicleId) {
          vehicleId = this.sosAlertData.vehicleId
          console.log('✅ VehicleId encontrado en vehicleId:', vehicleId)
        }
      }

      if (!vehicleId) {
        console.error('❌ No se pudo obtener vehicleId de ninguna manera')
        console.error('Campos disponibles en sosAlertData:', Object.keys(this.sosAlertData))
        return
      }

      const targetRoute = {
        name: 'AppVehicleViwer',
        params: { vehicle_id: vehicleId.toString() }
      }

      console.log('🚗 Navegando a vehículo con ID:', vehicleId)
      console.log('🚗 Ruta objetivo:', targetRoute)
      console.log('🚗 Ruta actual:', this.$route)

      // Verificar si ya estamos en la misma ruta
      const currentRoute = this.$route
      const isSameRoute = currentRoute.name === targetRoute.name &&
                         currentRoute.params.vehicle_id === targetRoute.params.vehicle_id

      if (isSameRoute) {
        console.log('🚗 Ya estamos en la vista del vehículo:', vehicleId)
        return
      }

      // Si estamos en AppVehicleViewer pero con diferente vehicle_id,
      // necesitamos forzar la recarga de datos
      if (currentRoute.name === 'AppVehicleViwer') {
        console.log('🔄 Cambiando a diferente vehículo, forzando recarga de datos...')

        // Primero cambiar la ruta
        this.$router.push(targetRoute).then(() => {
          console.log('✅ Ruta actualizada, buscando componente AppVehicleViewer...')

          // Buscar el componente AppVehicleViewer de forma más robusta
          this.$nextTick(() => {
            this.findAndUpdateVehicleViewer(vehicleId.toString())
          })
        }).catch(err => {
          if (err.name !== 'NavigationDuplicated') {
            console.error('❌ Error en navegación:', err)
          }
        })
        return
      }

      // Navegación normal (primera vez o desde otra vista)
      this.$router.push(targetRoute).then(() => {
        console.log('✅ Navegación exitosa a vehículo:', vehicleId)
      }).catch(err => {
        if (err.name === 'NavigationDuplicated') {
          console.log('🚗 Navegación duplicada ignorada para vehículo:', vehicleId)
        } else {
          console.error('❌ Error de navegación:', err)
        }
      })
    },

            async dismissAlert() {
      // Enviar evento de silenciamiento antes de desactivar
      await this.sendSosSilencedEvent()

      // Resetear el formulario si está abierto
      if (this.showManageForm) {
        this.showManageForm = false
        this.resetManageForm()
      }

      // Desactivar la alerta
      this.deactivateSosAlert()
    },

    playAlertSound() {
      // Crear un sonido de alerta simple usando Web Audio API
      try {
        const audioContext = new (window.AudioContext || window.webkitAudioContext)()
        const oscillator = audioContext.createOscillator()
        const gainNode = audioContext.createGain()

        oscillator.connect(gainNode)
        gainNode.connect(audioContext.destination)

        oscillator.frequency.value = 880 // Frecuencia de la nota
        oscillator.type = 'sine'

        gainNode.gain.setValueAtTime(0.3, audioContext.currentTime)
        gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.5)

        oscillator.start(audioContext.currentTime)
        oscillator.stop(audioContext.currentTime + 0.5)

        // Repetir el sonido 3 veces
        setTimeout(() => this.playBeep(audioContext), 600)
        setTimeout(() => this.playBeep(audioContext), 1200)
      } catch (e) {
        console.log('No se pudo reproducir el sonido de alerta:', e)
      }
    },

          playBeep(audioContext) {
        const oscillator = audioContext.createOscillator()
        const gainNode = audioContext.createGain()

        oscillator.connect(gainNode)
        gainNode.connect(audioContext.destination)

        oscillator.frequency.value = 880
        oscillator.type = 'sine'

        gainNode.gain.setValueAtTime(0.3, audioContext.currentTime)
        gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.5)

        oscillator.start(audioContext.currentTime)
        oscillator.stop(audioContext.currentTime + 0.5)
            },

      /**
       * Obtiene o genera un ID único del cliente
       */
      getClientId() {
        let clientId = localStorage.getItem('pusher_client_id')
        if (!clientId) {
          clientId = 'client_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9)
          localStorage.setItem('pusher_client_id', clientId)
        }
        return clientId
      },

      /**
       * Obtiene el ID del usuario actual
       */
      getUserId() {
        const userProfile = this.$store.getters['auth/getProfile']
        return (userProfile && userProfile.sub) || (userProfile && userProfile.id) || 'anonymous'
      },

                        /**
       * Envía evento de silenciamiento de alerta SOS
       * Envía una petición HTTP al webhook
       */
      async sendSosSilencedEvent() {
        try {
          const webhookUrl = 'https://c2q0025gnc.execute-api.us-east-1.amazonaws.com/prod/pusher-webhook'

          // Obtener datos del evento SOS
          const sosData = this.sosAlertData || {}

          // Validar campos requeridos
          if (!sosData.sos_id) {
            console.error('❌ No se puede enviar silenciamiento: falta sos_id')
            return
          }

          const userId = this.getUserId()
          if (!userId || userId === 'anonymous') {
            console.error('❌ No se puede enviar silenciamiento: usuario no autenticado')
            return
          }

          const silencedData = {
            type: 'silence',                              // ⚠️ IMPORTANTE: Especificar el tipo
            sos_id: sosData.sos_id,                       // ID del SOS a silenciar
            user_id: userId,                              // ID del usuario (requerido)
            action_type: 'silence',                       // Tipo de acción (silence/dismiss/acknowledge)
            reason: 'Acción del usuario'                  // Razón opcional
            // NO incluimos notification_id para evitar confusión con confirmación de recepción
          }

          const response = await fetch(webhookUrl, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json'
            },
            body: JSON.stringify(silencedData)
          })

          if (response.ok) {
            const result = await response.json()
            console.log('🔇 SOS Silenciado exitosamente:', {
              request: silencedData,
              response: result
            })
          } else {
            const errorText = await response.text()
            console.error('❌ Error al silenciar SOS:', {
              status: response.status,
              error: errorText,
              request: silencedData
            })
          }
        } catch (error) {
          console.error('Error enviando confirmación de silenciamiento:', error)
        }
      },

            /**
       * Guarda los datos del formulario de gestión
       * Envía la información al backend para registrar la gestión del SOS
       */
      async saveManageForm() {
        // Prevenir múltiples envíos
        if (this.isSavingManagement) return

        try {
          // Activar estado de carga
          this.isSavingManagement = true

          // Obtener datos necesarios
          const sosId = this.sosAlertData && this.sosAlertData.sos_id
          const userId = this.getUserId()

          // Validar datos requeridos
          if (!sosId) {
            console.error('❌ No se puede guardar la gestión: falta sos_id')
            this.isSavingManagement = false
            return
          }

          if (!userId || userId === 'anonymous') {
            console.error('❌ No se puede guardar la gestión: usuario no autenticado')
            this.isSavingManagement = false
            return
          }

          // Validar campos obligatorios
          if (!this.manageFormData.alert_type) {
            console.error('❌ No se puede guardar la gestión: falta clasificación de alerta')
            this.isSavingManagement = false
            return
          }

          if (!this.manageFormData.actions_taken || this.manageFormData.actions_taken.trim() === '') {
            console.error('❌ No se puede guardar la gestión: falta descripción de acciones tomadas')
            this.isSavingManagement = false
            return
          }

          // Preparar datos para enviar
          const managementData = {
            sos_id: sosId,
            user_id: userId,
            alert_type: this.manageFormData.alert_type,
            notes: this.manageFormData.actions_taken,
            status: 'in_progress',
            priority: this.manageFormData.alert_type === 'real_emergency' ? 'high' : 'medium',
            client_id: this.getClientId()
          }

          // Si es emergencia real, agregar flags adicionales
          if (this.manageFormData.alert_type === 'real_emergency') {
            managementData.follow_up_required = true
          }

          console.log('📤 Enviando gestión de alerta:', managementData)

          // Hacer la llamada al backend
          const response = await fetch('https://c2q0025gnc.execute-api.us-east-1.amazonaws.com/prod/sos-management', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'X-Environment': 'testing' // Cambiar a 'production' cuando sea necesario
            },
            body: JSON.stringify(managementData)
          })

          const result = await response.json()

                    if (response.ok && result.success) {
            console.log('✅ Gestión de alerta guardada exitosamente:', result)

            // Cerrar el formulario
            this.showManageForm = false

            // Resetear el formulario
            this.resetManageForm()

            // Desactivar la alerta SOS ya que fue gestionada
            this.deactivateSosAlert()

            // Opcional: Mostrar notificación de éxito
            // this.$store.dispatch('notifications/showSuccess', 'Gestión guardada correctamente')
          } else {
            console.error('❌ Error al guardar la gestión:', result)
            // Opcional: Mostrar notificación de error
            // this.$store.dispatch('notifications/showError', 'Error al guardar la gestión')
          }
        } catch (error) {
          console.error('❌ Error al enviar gestión de alerta:', error)
          // Opcional: Mostrar notificación de error
          // this.$store.dispatch('notifications/showError', 'Error de conexión al guardar')
        } finally {
          // Desactivar estado de carga
          this.isSavingManagement = false
        }
      },

      /**
       * Cancela el formulario de gestión
       */
      cancelManageForm() {
        this.showManageForm = false
        this.resetManageForm()
      },

      /**
       * Resetea los datos del formulario de gestión
       */
      resetManageForm() {
        this.manageFormData = {
          alert_type: '',
          actions_taken: ''
        }
      },

      /**
       * Navega a la alerta anterior
       */
      navigateToPrevious() {
        this.navigateToPreviousAlert()
      },

      /**
       * Navega a la siguiente alerta
       */
      navigateToNext() {
        this.navigateToNextAlert()
      },

      /**
       * Busca el componente AppVehicleViewer y fuerza la recarga de sus datos
       */
      findAndUpdateVehicleViewer(vehicleId) {
        console.log('🔍 Buscando componente AppVehicleViewer...')

        // Función recursiva para buscar el componente en el árbol de Vue
        const findComponent = (component, targetName) => {
          if (!component) return null

          // Verificar el componente actual
          if (component.$options && component.$options.name === targetName) {
            return component
          }

          // Buscar en los hijos
          if (component.$children && component.$children.length > 0) {
            for (let child of component.$children) {
              const found = findComponent(child, targetName)
              if (found) return found
            }
          }

          return null
        }

        // Buscar desde la raíz de la aplicación
        const vehicleViewerComponent = findComponent(this.$root, 'AppVehicleViewer')

        if (vehicleViewerComponent && vehicleViewerComponent.fetchVehicleData) {
          console.log('✅ Componente AppVehicleViewer encontrado, llamando fetchVehicleData...')

          try {
            vehicleViewerComponent.fetchVehicleData()
            console.log('✅ fetchVehicleData ejecutado exitosamente')
          } catch (error) {
            console.error('❌ Error ejecutando fetchVehicleData:', error)
          }
        } else {
          console.warn('⚠️ No se pudo encontrar el componente AppVehicleViewer o su método fetchVehicleData')
          console.log('🔄 Intentando actualización via store como fallback...')

          // Fallback: actualizar via store
          this.$store.dispatch('fetchVehicleById', vehicleId).then(() => {
            console.log('✅ Datos actualizados via store')
          }).catch(err => {
            console.error('❌ Error actualizando via store:', err)
          })
        }
      }
    },

    beforeDestroy() {
      // Limpiar clases del body si el componente se destruye
      document.body.classList.remove('sos-alert-active')
    }
}
</script>

<style scoped>
/* Contenedor principal */
.sos-alert-container {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 99999;
  pointer-events: none;
}

/* Overlay con efecto intermitente */
.sos-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(255, 0, 0, 0.15);
  animation: sos-flash 0.5s infinite alternate;
  pointer-events: none;
}

/* Panel de alerta */
.sos-alert-panel {
  position: absolute;
  top: 20px;
  left: 50%;
  transform: translateX(-50%);
  background: linear-gradient(135deg, #ff0000, #cc0000);
  color: white;
  padding: 20px;
  border-radius: 10px;
  box-shadow: 0 10px 50px rgba(0, 0, 0, 0.5);
  min-width: 500px;
  max-width: 90%;
  max-height: 90vh;
  overflow-y: auto;
  pointer-events: all;
  animation: sos-pulse 1s infinite;
}

/* Estilo personalizado para la barra de scroll del panel */
.sos-alert-panel::-webkit-scrollbar {
  width: 8px;
}

.sos-alert-panel::-webkit-scrollbar-track {
  background: rgba(0, 0, 0, 0.2);
  border-radius: 4px;
}

.sos-alert-panel::-webkit-scrollbar-thumb {
  background: rgba(255, 255, 255, 0.3);
  border-radius: 4px;
}

.sos-alert-panel::-webkit-scrollbar-thumb:hover {
  background: rgba(255, 255, 255, 0.5);
}

.sos-content {
  display: flex;
  align-items: center;
  gap: 20px;
}

.sos-icon-container {
  flex-shrink: 0;
}

.sos-icon {
  font-size: 48px;
  animation: sos-rotate 2s linear infinite;
}

.sos-message {
  flex-grow: 1;
}

.sos-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 10px;
}

.sos-title {
  margin: 0;
  font-size: 24px;
  font-weight: bold;
  text-transform: uppercase;
  animation: sos-text-flash 0.5s infinite alternate;
  flex-grow: 1;
}

.sos-navigation {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 8px;
  margin-left: 20px;
}

.sos-counter {
  background: rgba(255, 255, 255, 0.2);
  padding: 4px 12px;
  border-radius: 15px;
  font-size: 12px;
  font-weight: bold;
  color: white;
  white-space: nowrap;
}

.sos-nav-buttons {
  display: flex;
  gap: 4px;
}

.sos-nav-button {
  background: rgba(255, 255, 255, 0.9);
  border: none;
  border-radius: 50%;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.3s;
  color: #cc0000;
}

.sos-nav-button:hover:not(:disabled) {
  background: white;
  transform: scale(1.1);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.3);
}

.sos-nav-button:disabled {
  background: rgba(255, 255, 255, 0.3);
  color: rgba(255, 255, 255, 0.5);
  cursor: not-allowed;
}

.sos-nav-button i {
  font-size: 18px;
}

.sos-subtitle {
  margin: 0 0 15px 0;
  font-size: 16px;
  opacity: 0.95;
}

.sos-details {
  background: rgba(0, 0, 0, 0.2);
  padding: 10px;
  border-radius: 5px;
  margin-top: 10px;
}

.sos-details p {
  margin: 5px 0;
  font-size: 14px;
}

.sos-timestamp {
  font-weight: bold;
}

.sos-actions {
  display: flex;
  flex-direction: column;
  gap: 10px;
  flex-shrink: 0;
}

.sos-actions-primary {
  display: flex;
  justify-content: center;
}

.sos-actions-secondary {
  display: flex;
  gap: 10px;
  justify-content: center;
}

.sos-dismiss-button,
.sos-manage-button {
  background: white;
  border: none;
  padding: 8px 16px;
  border-radius: 5px;
  cursor: pointer;
  font-weight: bold;
  display: flex;
  align-items: center;
  gap: 5px;
  transition: all 0.3s;
  font-size: 13px;
}

.sos-locate-button {
  background: linear-gradient(135deg, #ff6b35, #ff4757);
  color: white;
  border: none;
  padding: 12px 24px;
  border-radius: 8px;
  cursor: pointer;
  font-weight: bold;
  display: flex;
  align-items: center;
  gap: 8px;
  transition: all 0.3s;
  font-size: 14px;
  box-shadow: 0 4px 12px rgba(255, 71, 87, 0.3);
}

.sos-dismiss-button {
  color: #cc0000;
}

.sos-manage-button {
  color: #0088ff;
}


.sos-dismiss-button:hover {
  background: #ffeeee;
  transform: scale(1.05);
}

.sos-manage-button:hover {
  background: #e6f0ff;
  transform: scale(1.05);
}

.sos-locate-button:hover {
  background: linear-gradient(135deg, #ff4757, #ff3742);
  transform: translateY(-2px);
  box-shadow: 0 6px 16px rgba(255, 71, 87, 0.4);
}

.sos-dismiss-button i,
.sos-manage-button i {
  font-size: 18px;
}

.sos-locate-button i {
  font-size: 20px;
}

/* Formulario de gestión */
.sos-manage-form {
  background: rgba(0, 0, 0, 0.3);
  border-radius: 8px;
  padding: 20px;
  margin-top: 20px;
}

.sos-form-divider {
  height: 2px;
  background: linear-gradient(to right, transparent, rgba(255, 255, 255, 0.5), transparent);
  margin-bottom: 20px;
}

.sos-manage-title {
  margin: 0 0 20px 0;
  color: #ffffff;
  font-size: 20px;
  font-weight: bold;
  text-align: center;
  text-shadow: 0 2px 4px rgba(0, 0, 0, 0.3);
}

.sos-form-group {
  margin-bottom: 20px;
}

.sos-form-label {
  display: block;
  margin-bottom: 8px;
  font-weight: bold;
  color: #ffffff;
  opacity: 0.95;
}

.required-field {
  color: #ff4757;
  font-weight: bold;
}

.sos-form-select,
.sos-form-textarea {
  width: 100%;
  padding: 10px;
  border: 2px solid #0066cc;
  border-radius: 5px;
  font-size: 14px;
  transition: all 0.3s;
  box-sizing: border-box;
  background-color: #0f0f1e;
  color: #ffffff;
}

.sos-form-select:focus,
.sos-form-textarea:focus {
  outline: none;
  border-color: #00b4ff;
  background-color: #1a1a3e;
  box-shadow: 0 0 8px rgba(0, 180, 255, 0.3);
}

.sos-form-textarea {
  resize: vertical;
  min-height: 100px;
  font-family: inherit;
}

.sos-form-select option {
  background-color: #1a1a2e;
  color: #ffffff;
}

.sos-form-textarea::placeholder {
  color: #8888aa;
}

.sos-form-actions {
  display: flex;
  gap: 10px;
  justify-content: center;
  margin-top: 25px;
}

.sos-save-button,
.sos-cancel-button {
  padding: 10px 25px;
  border: none;
  border-radius: 5px;
  font-weight: bold;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 8px;
  transition: all 0.3s;
  font-size: 14px;
}

.sos-save-button {
  background: white;
  color: #cc0000;
  border: 2px solid white;
  font-weight: bold;
}

.sos-save-button:hover:not(:disabled) {
  background: #f0f0f0;
  border-color: #f0f0f0;
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(255, 255, 255, 0.3);
}

.sos-save-button:disabled {
  background: rgba(255, 255, 255, 0.3);
  border-color: rgba(255, 255, 255, 0.3);
  color: rgba(255, 255, 255, 0.7);
  cursor: not-allowed;
  opacity: 0.6;
}

.sos-cancel-button {
  background: transparent;
  color: white;
  border: 2px solid rgba(255, 255, 255, 0.7);
}

.sos-cancel-button:hover {
  background: rgba(255, 255, 255, 0.1);
  border-color: white;
  transform: translateY(-2px);
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.3);
}

/* Animación slide-down */
.slide-down-enter-active,
.slide-down-leave-active {
  transition: all 0.3s ease;
}

.slide-down-enter,
.slide-down-leave-to {
  max-height: 0;
  opacity: 0;
  transform: translateY(-10px);
}

/* Bordes intermitentes */
.sos-border {
  position: absolute;
  background: linear-gradient(90deg, transparent, red, transparent);
  animation: sos-border-flash 1s linear infinite;
}

.sos-border-top,
.sos-border-bottom {
  left: 0;
  right: 0;
  height: 5px;
}

.sos-border-top {
  top: 0;
}

.sos-border-bottom {
  bottom: 0;
}

.sos-border-left,
.sos-border-right {
  top: 0;
  bottom: 0;
  width: 5px;
}

.sos-border-left {
  left: 0;
}

.sos-border-right {
  right: 0;
}

/* Animaciones */
@keyframes sos-flash {
  0% {
    background-color: rgba(255, 0, 0, 0.1);
  }
  100% {
    background-color: rgba(255, 0, 0, 0.3);
  }
}

@keyframes rotate {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}

.rotating {
  animation: rotate 1s linear infinite;
}

@keyframes sos-pulse {
  0% {
    transform: translateX(-50%) scale(1);
  }
  50% {
    transform: translateX(-50%) scale(1.02);
  }
  100% {
    transform: translateX(-50%) scale(1);
  }
}

@keyframes sos-rotate {
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
}

@keyframes sos-text-flash {
  0% {
    opacity: 1;
  }
  100% {
    opacity: 0.7;
  }
}

@keyframes sos-border-flash {
  0% {
    opacity: 0;
  }
  50% {
    opacity: 1;
  }
  100% {
    opacity: 0;
  }
}

/* Transición de fade */
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s;
}

.fade-enter,
.fade-leave-to {
  opacity: 0;
}

/* Media queries para responsividad */
@media (max-width: 768px) {
  .sos-alert-panel {
    min-width: 90%;
    padding: 15px;
  }

  .sos-content {
    flex-direction: column;
    text-align: center;
  }

  .sos-header {
    flex-direction: column;
    align-items: center;
    gap: 10px;
  }

  .sos-navigation {
    margin-left: 0;
    align-items: center;
  }

  .sos-title {
    font-size: 20px;
    text-align: center;
  }

  .sos-icon {
    font-size: 36px;
  }

  .sos-actions-secondary {
    flex-direction: column;
    gap: 8px;
  }

  .sos-dismiss-button,
  .sos-manage-button {
    width: 100%;
    justify-content: center;
  }

  .sos-locate-button {
    width: 100%;
    justify-content: center;
  }

    .sos-manage-form {
    padding: 15px;
    margin-top: 15px;
  }

  .sos-form-actions {
    flex-direction: column;
  }

  .sos-save-button,
  .sos-cancel-button {
    width: 100%;
    justify-content: center;
  }
}
</style>

<style>
/* Estilos globales cuando la alerta está activa */
body.sos-alert-active {
  animation: body-flash 0.5s infinite alternate;
}

@keyframes body-flash {
  0% {
    filter: brightness(1);
  }
  100% {
    filter: brightness(1.1) hue-rotate(-10deg);
  }
}
</style>



// WEBPACK FOOTER //
// src/components/SOSAlert.vue